import { useState, useEffect, useCallback } from 'react';
import { offlineManager, OfflineState } from '../utils/offline/manager';
import { offlineDB } from '../utils/offline/indexedDB';

export function useOffline() {
  const [state, setState] = useState<OfflineState>(offlineManager.getState());

  useEffect(() => {
    return offlineManager.subscribe(setState);
  }, []);

  const syncNow = useCallback(async () => {
    await offlineManager.syncPendingItems();
  }, []);

  const clearData = useCallback(async () => {
    await offlineManager.clearOfflineData();
  }, []);

  const queueMutation = useCallback(async (
    action: 'create' | 'update' | 'delete',
    endpoint: string,
    data: any
  ) => {
    await offlineManager.queueMutation(action, endpoint, data);
  }, []);

  return {
    ...state,
    syncNow,
    clearData,
    queueMutation
  };
}

export function useOfflineItemCache<T>(key: string, fetcher: () => Promise<T>, ttlSeconds: number = 300) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async (forceRefresh: boolean = false) => {
    setLoading(true);
    setError(null);

    try {
      if (!forceRefresh && navigator.onLine) {
        const cached = await offlineDB.getCache(key);
        if (cached) {
          setData(cached.data as T);
          setLoading(false);
          return;
        }
      }

      const freshData = await fetcher();
      setData(freshData);
      await offlineDB.setCache(key, freshData, ttlSeconds);
    } catch (e) {
      const cached = await offlineDB.getCache(key);
      if (cached) {
        setData(cached.data as T);
      } else {
        setError(e as Error);
      }
    } finally {
      setLoading(false);
    }
  }, [key, fetcher, ttlSeconds]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = useCallback(() => load(true), [load]);

  return { data, loading, error, refresh };
}

export default useOffline;